import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Download,
  FileText,
  History,
  Link2,
  Loader2,
  Plus,
  Save,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import BlockCard from "@/components/BlockCard";
import DocPreview from "@/components/DocPreview";
import StatusBadge from "@/components/StatusBadge";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { track } from "@/lib/meta";

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const [proposal, setProposal] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [title, setTitle] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [catalogue, setCatalogue] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [sharePassword, setSharePassword] = useState("");
  const [shareDays, setShareDays] = useState(30);
  const [shareToken, setShareToken] = useState("");
  const [kits, setKits] = useState([]);
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/proposals/${id}`);
      setProposal(data);
      setBlocks(data.blocks || []);
      setTitle(data.title);
      setShareToken(data.share?.revoked ? "" : data.share?.token || "");
      setDirty(false);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/proposals");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    api.get("/blocks/catalogue").then((r) => setCatalogue(r.data));
    api.get("/brand-kits").then((r) => setKits(r.data));
  }, [load]);

  const save = useCallback(
    async (label) => {
      setSaving(true);
      try {
        const { data } = await api.put(`/proposals/${id}`, { blocks, title, label });
        setProposal(data);
        setDirty(false);
        return data;
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setSaving(false);
      }
    },
    [blocks, id, title]
  );

  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => save("Autosave"), 2500);
    return () => clearTimeout(t);
  }, [dirty, save]);

  const mutate = (next) => {
    setBlocks(next);
    setDirty(true);
  };

  const patchBlock = (index, block) => mutate(blocks.map((b, i) => (i === index ? block : b)));
  const deleteBlock = (index) => mutate(blocks.filter((_, i) => i !== index));
  const duplicateBlock = (index) => {
    const copy = { ...JSON.parse(JSON.stringify(blocks[index])), id: `${Date.now()}` };
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    mutate(next);
  };

  const onDrop = (to) => {
    const from = dragFrom.current;
    setDragOver(null);
    if (from === null || from === to) return;
    const next = [...blocks];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    dragFrom.current = null;
    mutate(next);
  };

  const addBlock = async (type) => {
    try {
      if (dirty) await save("Autosave");
      const { data } = await api.post(`/proposals/${id}/blocks`, { type });
      setBlocks(data.blocks);
      setShowAdd(false);
      toast.success("Block added");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleLock = async (block) => {
    try {
      if (dirty) await save("Autosave");
      const { data } = await api.post(`/proposals/${id}/blocks/${block.id}/lock`);
      setBlocks(data.blocks);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const extractContent = (block) => {
    const d = block.data || {};
    if (block.type === "text") return d.html || "";
    if (block.type === "heading") return d.text || "";
    if (block.type === "bullets") return (d.items || []).join("\n");
    if (block.type === "terms") return (d.clauses || []).map((c) => `${c.title}: ${c.body}`).join("\n");
    if (block.type === "case_study") return d.body || "";
    if (block.type === "team_member") return d.bio || "";
    return "";
  };

  const applyContent = (block, content) => {
    const d = { ...(block.data || {}) };
    if (block.type === "text") d.html = content.startsWith("<") ? content : `<p>${content}</p>`;
    else if (block.type === "heading") d.text = content;
    else if (block.type === "bullets") d.items = content.split("\n").map((s) => s.replace(/^[-•*]\s*/, "")).filter(Boolean);
    else if (block.type === "terms")
      d.clauses = content
        .split("\n")
        .filter(Boolean)
        .map((l) => {
          const [t, ...rest] = l.split(":");
          return { title: t.trim(), body: rest.join(":").trim() };
        });
    else if (block.type === "case_study") d.body = content;
    else if (block.type === "team_member") d.bio = content;
    return { ...block, data: d };
  };

  const runAI = async (index, action) => {
    const block = blocks[index];
    const content = extractContent(block);
    if (!content.trim()) return toast.error("Nothing to work with in this block yet");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/section-action", { action, content });
      mutate(blocks.map((b, i) => (i === index ? applyContent(b, data.content) : b)));
      toast.success(`AI ${action} applied`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const scanFlags = async () => {
    try {
      if (dirty) await save("Autosave");
      const { data } = await api.post(`/ai/proposals/${id}/scan`);
      setProposal((p) => ({ ...p, unverified: data.unverified }));
      toast[data.unverified.length ? "warning" : "success"](
        data.unverified.length ? `${data.unverified.length} claim(s) need verification` : "No unverified claims found"
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resolveFlags = async () => {
    await api.post(`/proposals/${id}/resolve-flags`);
    setProposal((p) => ({ ...p, unverified: [] }));
    toast.success("Flags cleared");
  };

  const openVersions = async () => {
    const { data } = await api.get(`/proposals/${id}/versions`);
    setVersions(data);
    setShowVersions(true);
  };

  const restore = async (vid) => {
    const { data } = await api.post(`/proposals/${id}/versions/${vid}/restore`);
    setProposal(data);
    setBlocks(data.blocks);
    setShowVersions(false);
    toast.success("Version restored");
  };

  const createShare = async () => {
    try {
      if (dirty) await save("Autosave");
      const { data } = await api.post(`/proposals/${id}/share`, {
        password: sharePassword || null,
        expires_in_days: Number(shareDays) || 30,
      });
      setShareToken(data.token);
      await load();
      track("ProposalSent", { content_ids: [id], content_name: title, currency: proposal?.currency || "INR",
                              value: (proposal?.total_minor || 0) / 100 });
      toast.success("Share link ready");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const revokeShare = async () => {
    await api.post(`/proposals/${id}/share/revoke`);
    setShareToken("");
    await load();
    toast.success("Link revoked");
  };

  const patchMeta = async (payload) => {
    const { data } = await api.patch(`/proposals/${id}`, payload);
    setProposal(data);
  };

  const download = async (kind) => {
    const toastId = toast.loading(`Preparing your ${kind.toUpperCase()}…`);
    try {
      if (dirty) await save("Autosave");
      await downloadFile(`/proposals/${id}/export.${kind}`, `${title}.${kind}`);
      track("ProposalExported", { content_ids: [id], content_name: title, format: kind });
      toast.success(`${kind.toUpperCase()} downloaded`, { id: toastId });
    } catch (e) {
      toast.error(apiError(e), { id: toastId });
    }
  };

  if (!proposal)
    return (
      <div className="min-h-screen grid place-items-center bg-app text-zinc-600">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  const flaggedIds = new Set((proposal.unverified || []).map((f) => f.block_id));
  const shareUrl = shareToken ? `${window.location.origin}/p/${shareToken}` : "";
  const groups = catalogue.reduce((acc, b) => {
    (acc[b.group] = acc[b.group] || []).push(b);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-app text-zinc-100">
      <header className="sticky top-0 z-40 nv-glass border-b border-zinc-800/80">
        <div className="px-4 py-3 flex items-center gap-3">
          <button data-testid="editor-back-btn" onClick={() => navigate("/proposals")} className="p-2 rounded-lg hover:bg-zinc-800 transition-colors">
            <ArrowLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <Input
            data-testid="editor-title-input"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            className="h-9 max-w-md bg-transparent border-transparent hover:border-zinc-800 focus-visible:border-zinc-800 text-base font-semibold px-2"
          />
          <StatusBadge status={proposal.status} testid="editor-status-badge" />
          <span data-testid="autosave-indicator" className="text-[11px] font-num text-zinc-500 hidden sm:inline">
            {saving ? "Saving…" : dirty ? "Unsaved changes" : "All changes saved"}
          </span>

          <div className="ml-auto flex items-center gap-2">
            <Button data-testid="ai-scan-btn" variant="outline" size="sm" onClick={scanFlags} className="h-9 border-zinc-800 text-zinc-300 hidden sm:flex">
              <ShieldAlert className="w-4 h-4 mr-1.5" /> Fact check
            </Button>
            <Button data-testid="versions-btn" variant="outline" size="sm" onClick={openVersions} className="h-9 border-zinc-800 text-zinc-300 hidden sm:flex">
              <History className="w-4 h-4 mr-1.5" /> History
            </Button>
            <Button data-testid="export-pdf-btn" variant="outline" size="sm" onClick={() => download("pdf")} className="h-9 border-zinc-800 text-zinc-300">
              <Download className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button data-testid="export-docx-btn" variant="outline" size="sm" onClick={() => download("docx")} className="h-9 border-zinc-800 text-zinc-300 hidden sm:flex">
              <FileText className="w-4 h-4 mr-1.5" /> Word
            </Button>
            <Button data-testid="editor-save-btn" variant="outline" size="sm" onClick={() => save("Manual save")} className="h-9 border-zinc-800 text-zinc-300">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
            <Button data-testid="share-btn" size="sm" onClick={() => setShowShare(true)} className="h-9 bg-indigo-500 hover:bg-indigo-600 text-white">
              <Link2 className="w-4 h-4 mr-1.5" /> Send
            </Button>
          </div>
        </div>
      </header>

      {(proposal.unverified || []).length > 0 && (
        <div data-testid="hallucination-guard-banner" className="bg-amber-500/10 border-b border-amber-500/25 px-4 py-2.5 flex flex-wrap items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-amber-400" />
          <p className="text-xs text-amber-200">
            {proposal.unverified.length} claim(s) need verification before sending:{" "}
            <span className="font-num">{proposal.unverified.slice(0, 4).map((f) => f.snippet).join(", ")}</span>
          </p>
          <Button data-testid="resolve-flags-btn" size="sm" variant="outline" onClick={resolveFlags} className="h-7 ml-auto border-amber-500/40 text-amber-200 hover:bg-amber-500/15">
            I verified these
          </Button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)]">
        <div data-testid="block-editor-canvas" className="w-full lg:w-[42%] xl:w-[38%] h-full overflow-y-auto nv-scroll border-r border-zinc-800/80 p-4 space-y-2.5">
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              data-testid="editor-brandkit-select"
              value={proposal.brand_kit_id || ""}
              onChange={(e) => patchMeta({ brand_kit_id: e.target.value })}
              className="h-9 flex-1 min-w-[140px] rounded-lg bg-zinc-900/70 border border-zinc-800 px-2 text-xs text-zinc-300"
            >
              {kits.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
            <select
              data-testid="editor-currency-select"
              value={proposal.currency}
              onChange={(e) => patchMeta({ currency: e.target.value })}
              className="h-9 rounded-lg bg-zinc-900/70 border border-zinc-800 px-2 text-xs text-zinc-300"
            >
              {["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD"].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              data-testid="editor-gst-select"
              value={proposal.gst_mode}
              onChange={(e) => patchMeta({ gst_mode: e.target.value })}
              className="h-9 rounded-lg bg-zinc-900/70 border border-zinc-800 px-2 text-xs text-zinc-300"
            >
              <option value="none">No GST</option>
              <option value="cgst_sgst">CGST+SGST</option>
              <option value="igst">IGST</option>
              <option value="single">Single GST</option>
            </select>
          </div>

          {blocks.map((block, i) => (
            <BlockCard
              key={block.id}
              block={block}
              index={i}
              currency={proposal.currency}
              isAdmin={isAdmin}
              onPatch={patchBlock}
              onDelete={deleteBlock}
              onDuplicate={duplicateBlock}
              onToggleLock={toggleLock}
              onDragStart={(idx) => (dragFrom.current = idx)}
              onDragOver={setDragOver}
              onDrop={onDrop}
              onAI={runAI}
              aiBusy={aiBusy}
              dragOver={dragOver === i}
              flagged={flaggedIds.has(block.id)}
            />
          ))}

          <Button data-testid="add-block-btn" variant="outline" onClick={() => setShowAdd(true)} className="w-full h-10 border-dashed border-zinc-700 text-zinc-400 hover:text-indigo-300 hover:border-indigo-500/40">
            <Plus className="w-4 h-4 mr-1.5" /> Add block
          </Button>
          <div className="h-8" />
        </div>

        <div className="hidden lg:block flex-1 h-full overflow-y-auto nv-scroll bg-panel p-8">
          <div className="mb-4 flex items-center justify-between text-xs text-zinc-500">
            <span className="font-num uppercase tracking-[0.18em]">Live preview</span>
            <span className="font-num">{proposal.totals?.grand_total_formatted}</span>
          </div>
          <DocPreview proposal={{ ...proposal, blocks, title }} brand={proposal.brand_kit} orgName={user?.org_name} />
        </div>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-panel border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a block</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto nv-scroll">
            {Object.entries(groups).map(([group, items]) => (
              <div key={group}>
                <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500 mb-2">{group}</p>
                <div className="grid grid-cols-2 gap-2">
                  {items.map((b) => (
                    <button
                      key={b.type}
                      data-testid={`add-block-${b.type}`}
                      onClick={() => addBlock(b.type)}
                      className="text-left px-3 py-2.5 rounded-lg border border-zinc-800 hover:border-indigo-500/40 hover:bg-indigo-500/8 text-sm text-zinc-300 transition-colors"
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="bg-panel border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto nv-scroll divide-y divide-zinc-800/60">
            {!versions.length && <p className="text-sm text-zinc-500 py-4">No saved versions yet.</p>}
            {versions.map((v) => (
              <div key={v.id} className="py-2.5 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-zinc-200">{v.label}</p>
                  <p className="text-[11px] text-zinc-500 font-num">
                    {new Date(v.created_at).toLocaleString()} · {v.block_count} blocks
                  </p>
                </div>
                <Button data-testid={`restore-version-${v.id}`} size="sm" variant="outline" onClick={() => restore(v.id)} className="h-7 border-zinc-800 text-zinc-300">
                  Restore
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="bg-panel border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>Send to client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500">Password (optional)</label>
              <Input data-testid="share-password-input" value={sharePassword} onChange={(e) => setSharePassword(e.target.value)} placeholder="Leave blank for no password" className="mt-1 bg-zinc-900/70 border-zinc-800" />
            </div>
            <div>
              <label className="text-xs text-zinc-500">Expires in (days)</label>
              <Input data-testid="share-expiry-input" type="number" value={shareDays} onChange={(e) => setShareDays(e.target.value)} className="mt-1 bg-zinc-900/70 border-zinc-800 font-num" />
            </div>
            <Button data-testid="create-share-link-btn" onClick={createShare} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              {shareToken ? "Regenerate link" : "Generate secure link"}
            </Button>
            {shareUrl && (
              <div className="rounded-lg border border-zinc-800 p-3 space-y-2">
                <p data-testid="share-url" className="text-xs font-num text-indigo-300 break-all">{shareUrl}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="copy-share-link-btn"
                    onClick={() => {
                      navigator.clipboard?.writeText(shareUrl);
                      toast.success("Copied");
                    }}
                    className="border-zinc-800 text-zinc-300"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(shareUrl, "_blank")} className="border-zinc-800 text-zinc-300">
                    Preview
                  </Button>
                  <Button size="sm" variant="outline" data-testid="revoke-share-btn" onClick={revokeShare} className="border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto">
                    Revoke
                  </Button>
                </div>
              </div>
            )}
            {proposal.tracking?.opens > 0 && (
              <div data-testid="tracking-summary" className="text-xs text-zinc-400 rounded-lg bg-zinc-900/60 border border-zinc-800 p-3 space-y-1 font-num">
                <p>Opens: {proposal.tracking.opens}</p>
                <p>First opened: {proposal.tracking.first_opened_at ? new Date(proposal.tracking.first_opened_at).toLocaleString() : "—"}</p>
                <p>Time spent: {Math.round((proposal.tracking.seconds_spent || 0) / 60)} min</p>
                <p>Sections read: {(proposal.tracking.sections_read || []).length}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
